import { useState } from "react";
import { Banner, SectionHeading, passwordStrength } from "../settings/settingsUi";
import { PersonalAccountSecurityClient } from "@/api/personalAccountSecurityApi";
import TotpSettingsSection from "./TotpSettingsSection";

const securityClient = new PersonalAccountSecurityClient();

function PasswordField({ id, label, value, onChange, show, onToggle, placeholder, disabled }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-slate-800 dark:text-slate-200">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={show ? "text" : "password"}
          className="ui-field pr-10"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          autoComplete={id === "pw-current" ? "current-password" : "new-password"}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label={show ? "Hide password" : "Show password"}
        >
          <i className={show ? "ri-eye-off-line" : "ri-eye-line"} aria-hidden />
        </button>
      </div>
    </div>
  );
}

export default function SecuritySettingsSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);

  const strength = passwordStrength(next);
  const mismatch = confirm && next !== confirm;

  const save = async () => {
    if (!current || !next || !confirm) return;
    if (next !== confirm) {
      setBanner({ type: "err", text: "New passwords don't match." });
      return;
    }
    if (strength.score < 2) {
      setBanner({ type: "err", text: "Please choose a stronger password." });
      return;
    }
    setSaving(true);
    setBanner(null);
    try {
      const res = await securityClient.changePassword({
        currentPassword: current,
        newPassword: next,
      });
      setBanner({ type: "ok", text: res?.message || "Password updated successfully." });
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e) {
      const detail = e?.data?.detail;
      const message =
        (typeof detail === "object" && detail?.message) ||
        (typeof detail === "string" && detail) ||
        (e instanceof Error ? e.message : "Could not update password.");
      setBanner({ type: "err", text: message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h3 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Security</h3>
        <p className="mt-1 text-sm text-slate-500">
          Manage your password and optional two-factor authentication (2FA) for this workspace.
        </p>
      </div>

      <TotpSettingsSection />

      <div className="ui-card-static space-y-5 !p-6 sm:!p-8">
        <SectionHeading
          label="Change password"
          desc="Use a strong password you don't use anywhere else."
        />

        {banner && <Banner type={banner.type} text={banner.text} onDismiss={() => setBanner(null)} />}

        <div className="space-y-4">
          <PasswordField
            id="pw-current"
            label="Current password"
            value={current}
            onChange={setCurrent}
            show={showCurrent}
            onToggle={() => setShowCurrent((v) => !v)}
            placeholder="••••••••"
            disabled={saving}
          />
          <div>
            <PasswordField
              id="pw-new"
              label="New password"
              value={next}
              onChange={setNext}
              show={showNext}
              onToggle={() => setShowNext((v) => !v)}
              placeholder="Minimum 8 characters"
              disabled={saving}
            />
            {next && (
              <div className="mt-2 space-y-1">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <div
                      key={n}
                      className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                        n <= strength.score ? strength.color : "bg-slate-200 dark:bg-slate-700"
                      }`}
                    />
                  ))}
                </div>
                <p
                  className={`text-[11px] font-medium ${
                    strength.score >= 4
                      ? "text-emerald-700 dark:text-emerald-400"
                      : strength.score >= 3
                        ? "text-amber-600"
                        : "text-slate-500"
                  }`}
                >
                  {strength.label}
                </p>
              </div>
            )}
          </div>
          <PasswordField
            id="pw-confirm"
            label="Confirm new password"
            value={confirm}
            onChange={setConfirm}
            show={showNext}
            onToggle={() => setShowNext((v) => !v)}
            placeholder="Repeat new password"
            disabled={saving}
          />
          {mismatch && (
            <p className="text-[11px] font-medium text-rose-600">Passwords don&apos;t match.</p>
          )}
        </div>

        <button
          type="button"
          className="ui-btn-primary"
          onClick={() => void save()}
          disabled={saving || !current || !next || !confirm || !!mismatch}
        >
          {saving ? "Updating…" : "Update password"}
        </button>
      </div>
    </div>
  );
}
